import { GoogleGenerativeAI } from '@google/generative-ai';
import { handleGoogleAIError } from './error-handlers';
import { compressImage } from './image-compression';

const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GOOGLE_AI_API_KEY);

async function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export async function detectPlantDisease(imageData, userId) {
  try {
    // Validate image data
    if (!imageData || !imageData.includes('base64')) {
      throw new Error('Invalid image data provided');
    }

    // Compress image and convert back to base64
    const compressedBlob = await compressImage(imageData);
    if (!compressedBlob) {
      throw new Error('Failed to process image. Please try again.');
    }

    const compressedBase64 = await blobToBase64(compressedBlob);
    if (!compressedBase64) {
      throw new Error('Failed to process image. Please try again.');
    }

    const model = genAI.getGenerativeModel({ model: 'gemini-1.5-flash' });

    const prompt = `
      Analyze this plant image for disease detection. Provide a detailed report in this exact format:

      1. Plant Identification:
         - Species name (common and scientific)
         - Plant family
         - Confidence level of identification (%)

      2. Disease Assessment:
         - Disease name and classification
         - Confidence level of diagnosis (%)
         - Visual symptoms detected
         - Disease progression stage (Early/Moderate/Advanced)
         - Primary causes:
           * List environmental factors
           * List pathogenic factors
           * List nutritional factors

      3. Impact Analysis:
         - Severity level (Low/Medium/High)
         - Potential spread risk
         - Expected impact on plant health
         - Time sensitivity of treatment

      4. Treatment Recommendations:
         - Immediate actions required
         - Organic control methods
         - Chemical control options (if necessary)
         - Preventive measures
         - Required environmental adjustments

      5. Care Instructions:
         - Watering adjustments
         - Sunlight requirements
         - Soil pH recommendations
         - Nutrient supplementation
         - Environmental conditions

      6. Warning Indicators:
         - Expert consultation needed (Yes/No)
         - Risk to nearby plants (Low/Medium/High)
         - Critical time window for treatment
         - Special precautions

      If the image quality is insufficient for accurate analysis, respond with "QUALITY_INSUFFICIENT".
      If no disease is detected, respond with "NO_DISEASE_DETECTED" followed by general plant care recommendations.
      If you're not confident about the diagnosis (below 70%), start the response with "TENTATIVE_DIAGNOSIS:".
    `.trim();

    const result = await model.generateContent([
      prompt,
      {
        inlineData: {
          mimeType: 'image/jpeg',
          data: compressedBase64.split(',')[1]
        }
      }
    ]);

    if (!result?.response) {
      throw new Error('No response from AI service');
    }

    const response = await result.response;
    const text = response.text();

    // Handle special response cases
    if (text.includes('QUALITY_INSUFFICIENT')) {
      throw new Error(
        'Image quality is insufficient for accurate analysis. Please provide a clearer, well-lit image showing the affected areas.'
      );
    }

    if (text.includes('NO_DISEASE_DETECTED')) {
      return {
        status: 'healthy',
        message: 'No disease detected',
        recommendations: text.split('NO_DISEASE_DETECTED')[1].trim()
      };
    }

    if (text.includes('TENTATIVE_DIAGNOSIS:')) {
      return {
        status: 'tentative',
        message: 'Tentative diagnosis - please verify with a plant expert',
        analysis: text.split('TENTATIVE_DIAGNOSIS:')[1].trim()
      };
    }

    return {
      status: 'confirmed',
      analysis: text
    };
  } catch (error) {
    const errorMessage = handleGoogleAIError(error);
    throw new Error(errorMessage);
  }
}
